import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen'
import { Box, Tooltip } from '@mui/material'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { LuX } from 'react-icons/lu'
import { useDispatch } from 'react-redux'

import HtmlKonvaElement from '@/components/HtmlKonvaElement'
import Icon from '@/components/Icon'
import { getInitials } from '@/pages/stainingStudio/utils'
import { WASH_BAKE_BACKGROUNDS } from '@/pages/stainingStudio/utils/constants'
import { useAppSelector } from '@/store'
import {
  removeOpaquerStainingTarget,
  setOpaquerStainingOutput,
} from '@/store/pfmCrown'
import { getTooltipPlacement } from '@/utils/helper'

import CustomChip from '../Chip'
import LabelWithTooltip from '../LabelWithTooltip'
import BrandValue from './components/BrandValue'
import LabelValuePair from './components/LabelValuePair'

const lastTooltipMap: Record<string, { x: number; y: number }> = {}

const TooltipContent = React.memo(
  ({ output, setTooltipOpen }: { output: any; setTooltipOpen: () => void }) => {
    const colors = ['#68CEA1', '#FFB975']
    const [expanded, setExpanded] = useState(false)

    return (
      <Box
        display="flex"
        flexGrow={1}
        flexDirection="column"
        gap={1}
        alignItems="flex"
      >
        {expanded && (
          <>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={1}
            >
              <LabelWithTooltip
                titleSx={{ fontSize: '14px', whiteSpace: 'nowrap' }}
                tooltipSx={{ height: '16px', width: '16px' }}
                label="Staining Target in LAB"
                tooltipText="Apply the staining recipe on the framework and measure before baking it with Optishade to verify the staining target in LAB values. The Chroma ( a*+b*) should be very close to the target. The Luminosity will be higher before baking it because it will drop after firing it."
              />
              <Icon icon={LuX} size={18} onClick={setTooltipOpen} />
              <CloseFullscreenIcon
                onClick={() => setExpanded((prev) => !prev)}
                sx={{ height: 16, width: 16, cursor: 'pointer' }}
              />
            </Box>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <LabelValuePair
                label="L:"
                value={output?.LABafterstaining?.[0].toFixed(2)}
              />
              <LabelValuePair
                label="a:"
                value={output?.LABafterstaining?.[1].toFixed(2)}
              />
              <LabelValuePair
                label="b:"
                value={output?.LABafterstaining?.[2].toFixed(2)}
              />
            </Box>
          </>
        )}

        {output?.provided_input?.stainingbrand && (
          <>
            {expanded && (
              <Box sx={{ borderTop: '1px solid #999', width: '100%' }}></Box>
            )}
            <BrandValue
              value={output.provided_input.stainingbrand?.toUpperCase() || ''}
              expanded={expanded}
              setExpanded={setExpanded}
              setTooltipOpen={setTooltipOpen}
            />
          </>
        )}
        {expanded && output?.stainingrecipe?.stainingthickness && (
          <>
            <Box sx={{ borderTop: '1px solid #999', width: '100%' }}></Box>
            <LabelValuePair
              label="Staining Intensity"
              value={`${output?.stainingrecipe?.stainingthickness?.toFixed(2)}mm`}
            />
          </>
        )}

        {output?.stainingrecipe?.powders?.length && (
          <>
            <Box sx={{ borderTop: '1px solid #999', width: '100%' }}></Box>
            {output?.stainingrecipe?.powders?.map((p: string, i: number) => (
              <LabelValuePair
                key={`${p}-${output?.stainingrecipe?.ratios[i]}`}
                label={
                  <Box
                    height="16px"
                    width="16px"
                    borderRadius={50}
                    bgcolor={colors?.[i] || '#68cea1'}
                  ></Box>
                }
                value={`${output?.stainingrecipe?.ratios[i]} - ${p}`}
              />
            ))}
          </>
        )}
      </Box>
    )
  },
)

const TooltipBox = React.memo(
  ({
    keyName,
    data,
    output,
    open,
    toggle,
    close,
    onDelete,
  }: {
    keyName: string
    data: any
    output: any
    open: boolean
    toggle: (key: string) => void
    close: (key: string) => void
    onDelete: (key: string) => void
  }) => {
    const [placement, setPlacement] = useState<'right' | 'left'>('right')

    const canvasImage = useAppSelector((state) => state.app.canvasImage)

    const prevOutputRef = useRef<any>(null)

    useEffect(() => {
      const currentX = data.imagePosition?.x
      const currentY = data.imagePosition?.y

      const placementResult = getTooltipPlacement({
        keyName,
        currentX,
        currentY,
        open,
        canvasImageId: canvasImage?.id,
        output,
        prevOutputRef,
        lastTooltipMap,
      })

      if (placementResult) {
        setPlacement(placementResult)
      }
    }, [
      open,
      keyName,
      data.imagePosition?.x,
      data.imagePosition?.y,
      output,
      canvasImage?.id,
    ])
    return (
      <HtmlKonvaElement
        key={keyName}
        x={data.imagePosition?.x}
        y={data.imagePosition?.y}
      >
        <Box position="relative">
          <Box sx={{ transform: 'translate(-50%, -60%)' }}>
            <Tooltip
              open={open}
              title={
                output ? (
                  <TooltipContent
                    output={output}
                    setTooltipOpen={() => close(keyName)}
                  />
                ) : null
              }
              arrow
              placement={placement}
              slotProps={{
                popper: { disablePortal: true },
                tooltip: {
                  sx: {
                    bgcolor: 'rgba(33, 33, 33, 0.80)',
                    color: 'white',
                    padding: '10px',
                  },
                },
                arrow: { sx: { color: 'rgba(33, 33, 33, 0.80)' } },
              }}
            >
              <CustomChip
                onClick={() => toggle(keyName)}
                sx={{
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: 'black',
                  padding: '4px 8px',
                  background: WASH_BAKE_BACKGROUNDS[keyName],
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  lineHeight: '20px',
                  '&:hover': {
                    background: WASH_BAKE_BACKGROUNDS[keyName],
                  },
                  '& .MuiChip-label': {
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 8px 0 0px',
                    lineHeight: '20px',
                  },
                }}
                label={getInitials(keyName)}
                onDelete={() => onDelete(keyName)}
              />
            </Tooltip>
          </Box>
        </Box>
      </HtmlKonvaElement>
    )
  },
)

const KonvaOpaquerStaining = () => {
  const dispatch = useDispatch()
  const pfmCrown = useAppSelector((state) => state.pfmCrown.pfmCrown)

  const canvasImage = useAppSelector((state) => state.app.canvasImage)

  const opaquerStainingOutput = useAppSelector(
    (state) => state.pfmCrown.opaquerStainingOutput,
  )

  const opaquerStainingTargetColor = useAppSelector(
    (state) => state.pfmCrown.opaquerStainingTargetColor,
  )
  const [tooltipOpenMap, setTooltipOpenMap] = useState<Record<string, boolean>>(
    {},
  )

  useEffect(() => {
    const initialTooltipState: Record<string, boolean> = {}
    Object.keys(pfmCrown || {}).forEach((key) => {
      initialTooltipState[key] = true
    })
    setTooltipOpenMap(initialTooltipState)
  }, [canvasImage?.id, pfmCrown])

  const handleToggleTooltip = useCallback((key: string) => {
    setTooltipOpenMap((prev) => ({ ...prev, [key]: !prev[key] }))
  }, [])

  const handleCloseTooltip = useCallback((key: string) => {
    setTooltipOpenMap((prev) => ({ ...prev, [key]: false }))
  }, [])

  const handleDeleteOpaquerStaining = useCallback(
    (opaquerKey: string) => {
      if (!canvasImage?.id) return
      dispatch(
        setOpaquerStainingOutput({
          imageId: canvasImage?.id,
          key: opaquerKey,
          result: undefined,
        }),
      )
      dispatch(
        removeOpaquerStainingTarget({
          imageId: canvasImage.id,
          key: opaquerKey,
        }),
      )
    },
    [dispatch, canvasImage?.id],
  )
  return (
    <>
      {Object.keys(pfmCrown || {}).map((key) => {
        const data = opaquerStainingTargetColor?.[key]
        const output = opaquerStainingOutput?.[canvasImage?.id]?.[key]

        if (!output) return null
        return (
          <TooltipBox
            key={key}
            keyName={key}
            data={data}
            output={output}
            open={!!tooltipOpenMap[key]}
            toggle={handleToggleTooltip}
            close={handleCloseTooltip}
            onDelete={handleDeleteOpaquerStaining}
          />
        )
      })}
    </>
  )
}

export default KonvaOpaquerStaining
